import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, Loader2, Package, Save } from 'lucide-react';
import { platformAPI } from '../../services/platformApi';

const input =
  'w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white';

const errorText = (e) =>
  e?.response?.data?.error || e?.response?.data?.errors?.map((x) => `${x.path}: ${x.msg}`).join(', ') || e?.message;

/** `2026-10-25T…` → `2026-10-25`, for a date input. */
const dateOnly = (iso) => (iso ? String(iso).slice(0, 10) : '');

const PLAN_STYLES = {
  legacy: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200',
  trial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  base: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
};

export function PlanBadge({ plan }) {
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${PLAN_STYLES[plan] || 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'}`}>
      {plan || 'no plan'}
    </span>
  );
}

/**
 * The tenant's plan, add-ons, trial date and payments fee (Phase 7a).
 *
 * Saving sends only what changed; the server validates against the
 * catalogue, writes in one transaction, audits the diff and clears the
 * caches, so the tenant sees the change on its next request. Add-ons a
 * billing subscription granted are listed but not editable here.
 */
export default function PlanPanel({ tenant, onSaved }) {
  const queryClient = useQueryClient();
  const catalogue = useQuery({ queryKey: ['platform-catalogue'], queryFn: () => platformAPI.catalogue(), select: (r) => r.data });

  const operatorAddons = (tenant.addons || []).filter((a) => a.source === 'operator').map((a) => a.key);
  const otherAddons = (tenant.addons || []).filter((a) => a.source !== 'operator');
  const initial = () => ({
    plan: tenant.plan || '',
    addons: operatorAddons,
    trialEndsAt: dateOnly(tenant.trialEndsAt),
    platformFeeBps: String(tenant.platformFeeBps ?? 0),
    note: '',
  });
  const [form, setForm] = useState(initial);
  const [saved, setSaved] = useState(null);
  // A fresh tenant row (after a save, or a refresh) resets the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setForm(initial()), [tenant.plan, tenant.trialEndsAt, tenant.platformFeeBps, JSON.stringify(tenant.addons)]);

  const save = useMutation({
    mutationFn: (body) => platformAPI.setPlan(tenant.slug, body),
    onSuccess: (r) => {
      setSaved(r.data);
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      onSaved?.();
    },
  });

  const submit = (e) => {
    e.preventDefault();
    const body = {};
    if (form.plan && form.plan !== tenant.plan) body.plan = form.plan;
    if (JSON.stringify([...form.addons].sort()) !== JSON.stringify([...operatorAddons].sort())) body.addons = form.addons;
    if (form.plan === 'trial' && form.trialEndsAt !== dateOnly(tenant.trialEndsAt)) {
      body.trialEndsAt = form.trialEndsAt ? new Date(`${form.trialEndsAt}T23:59:59Z`).toISOString() : null;
    }
    if (Number(form.platformFeeBps) !== (tenant.platformFeeBps ?? 0)) body.platformFeeBps = Number(form.platformFeeBps);
    if (form.note.trim()) body.note = form.note.trim();
    save.mutate(body);
  };

  const toggleAddon = (key) =>
    setForm((f) => ({ ...f, addons: f.addons.includes(key) ? f.addons.filter((k) => k !== key) : [...f.addons, key] }));

  const modules = catalogue.data?.modules || [];
  const addonModules = modules.filter((m) => m.addon);
  const feeOk = /^\d+$/.test(form.platformFeeBps) && Number(form.platformFeeBps) <= 1000;
  const planInfo = catalogue.data?.plans?.find((p) => p.key === form.plan);

  return (
    <section>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
        <Package className="h-4 w-4" /> Plan <PlanBadge plan={tenant.plan} />
        {tenant.plan === 'trial' && tenant.trialEndsAt && (
          <span className="font-normal text-neutral-400">· trial ends {dateOnly(tenant.trialEndsAt)}</span>
        )}
      </h2>
      <form onSubmit={submit} className="space-y-4 rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
        {catalogue.error && <p className="text-sm text-red-600 dark:text-red-400">{errorText(catalogue.error)}</p>}
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Plan</label>
            <select className={input} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              {!form.plan && <option value="">— none (nothing entitled) —</option>}
              {(catalogue.data?.plans || []).map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
            {planInfo && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{planInfo.summary}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Trial ends</label>
            <input
              type="date"
              className={input}
              disabled={form.plan !== 'trial'}
              value={form.plan === 'trial' ? form.trialEndsAt : ''}
              onChange={(e) => setForm({ ...form, trialEndsAt: e.target.value })}
            />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              {form.plan === 'trial'
                ? `Empty means ${catalogue.data?.trialDays ?? 14} days from now. After it, the plan becomes Base.`
                : 'Only a trial has an end date.'}
            </p>
          </div>
          <div>
            <label
              htmlFor="platform-fee-bps"
              className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              title="Basis points: hundredths of a percent. 1 bps = 0.01%, 50 bps = 0.5%, 100 bps = 1%. Taken from each payment as Stripe's application fee, on top of Stripe's own processing fee, and only on platform payments (Stripe Connect). 0 takes no fee."
            >
              Platform payments fee (bps)
            </label>
            <input
              id="platform-fee-bps"
              type="number"
              min="0"
              max="1000"
              step="1"
              className={input}
              value={form.platformFeeBps}
              onChange={(e) => setForm({ ...form, platformFeeBps: e.target.value })}
            />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Basis points (hundredths of a percent) on platform payments only: 100 = 1%
              {/^\d+$/.test(form.platformFeeBps) && Number(form.platformFeeBps) > 0 ? `, so ${form.platformFeeBps} = ${(Number(form.platformFeeBps) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}% of each payment` : ''}. 0 to 1000; 0 takes no fee.
              Never charged to a tenant using its own merchant account or Stripe account. A change applies to new payments and subscriptions.
            </p>
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">Add-ons</p>
          {catalogue.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {addonModules.map((m) => {
                const inPlan = planInfo?.modules?.includes(m.key);
                return (
                  <label key={m.key} className="flex items-start gap-2 rounded-lg border border-neutral-200 p-2 text-sm dark:border-neutral-700">
                    <input type="checkbox" className="mt-1" checked={form.addons.includes(m.key)} onChange={() => toggleAddon(m.key)} />
                    <span>
                      <span className="font-medium text-neutral-900 dark:text-white">{m.label}</span>
                      {inPlan && <span className="ml-1 text-xs text-green-700 dark:text-green-300">(included in {planInfo.label})</span>}
                      <span className="block text-xs text-neutral-500 dark:text-neutral-400">{m.summary}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {otherAddons.length > 0 && (
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              Also held, granted by {[...new Set(otherAddons.map((a) => a.source))].join(' / ')} (not editable here):{' '}
              {otherAddons.map((a) => `${a.key}${a.expiresAt ? ` until ${dateOnly(a.expiresAt)}` : ''}`).join(', ')}
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Note (kept in the audit log)</label>
          <input className={input} maxLength={500} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. signed order 2026-10" />
        </div>

        {save.error && (
          <p className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4" /> {errorText(save.error)}
          </p>
        )}
        {saved && !save.isPending && (
          <p className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
            <CheckCircle className="h-4 w-4" />
            {saved.changed?.length ? `Saved: ${saved.changed.join(', ')}. ${saved.note}` : 'Nothing changed.'}
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={save.isPending || !feeOk || !form.plan}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save plan
          </button>
        </div>
      </form>
    </section>
  );
}
